import React from 'react';
import { TouchableOpacity } from 'react-native';

import { UploadProgressOverlay } from '@components/Chat/UploadProgressOverlay';
import Highlighter from '@components/HighlightWords';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Document, LocalFile } from '@graphql/generated';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import useSearch from '@hooks/useSearch';
import theme from '@themes/theme';
import { formatSize } from '@utils/fileUtils';
interface DocumentCardInterface {
  filterVal: string;
  document: Document | LocalFile;
  onPress?: (document: Document | LocalFile) => void;
  onLongPress?: (document: Document | LocalFile) => void;
  onDelete?: (document: LocalFile) => void;
  doNotUseGlobalSearch?: boolean;
  isActivityItem?: boolean;
  width?: number;
  height?: number;
}

const DocumentCard: React.FC<DocumentCardInterface> = ({
  filterVal = '',
  document,
  onPress,
  onLongPress,
  onDelete,
  doNotUseGlobalSearch = false,
  isActivityItem = false,
  width,
  height,
}) => {
  const { name, size, contentType } = document;
  const { search } = useSearch();
  const isLocal = document.clientId === document.id;
  let xfilterVal = filterVal ?? search;
  if (doNotUseGlobalSearch) xfilterVal = filterVal ?? '';

  // TA-10279: per-file upload progress for document attachments — this
  // composer previously showed nothing at all while a document uploaded
  // (the old `ActivityIndicatorLoading` here was gated on `!onDelete`, and
  // the composer always passes `onDelete`, so it never actually rendered).
  // Mirrors ChatImages.tsx's already-correct image/video handling via the
  // shared `UploadProgressOverlay`.
  const { retryUpload, uploadingFiles } = useChatUploadManager();
  const uploadingFile = uploadingFiles?.find(
    (f) => f.file.clientId === document.clientId
  );
  const status = uploadingFile?.status;
  const compressionProgress = uploadingFile?.compressionProgress ?? 0;
  const uploadProgress = uploadingFile?.progress ?? 0;
  const combinedProgress =
    status === 'compressing'
      ? compressionProgress * 0.5
      : 0.5 + uploadProgress * 0.5;
  if (isActivityItem) {
    return (
      <TouchableOpacity
        onPress={() => onPress?.(document)}
        onLongPress={() => onLongPress?.(document)}>
        <Box
          overflow='hidden'
          borderRadius='xs'
          borderColor='grey02'
          borderWidth={1}
          width={width ?? 118}>
          <Box
            width={width ?? 118}
            height={height ?? 118}
            alignItems='center'
            justifyContent='center'
            backgroundColor='grey01'>
            <Icon
              fileType={contentType || undefined}
              color='blue'
              variant='xl'
              marginBottom='xxs'
            />
            <Text
              variant='labelRequired'
              numberOfLines={1}
              maxWidth={(width ?? 118) - 16}
              color='textPrimary'>
              {name}
            </Text>
          </Box>
        </Box>
      </TouchableOpacity>
    );
  }
  return (
    <TouchableOpacity
      onPress={() => onPress?.(document)}
      onLongPress={() => onLongPress?.(document)}>
      <Box flexDirection='row' marginBottom='xs'>
        <Box
          accessibilityLabel='Message'
          flexDirection='row'
          alignItems='center'
          borderColor='grey02'
          borderWidth={1}
          borderRadius='xs'
          padding='xs'
          position='relative'
          maxWidth='100%'>
          <Icon fileType={contentType || 'unknown'} color='black' variant='l' />
          <Box marginLeft='xs' flex={1}>
            <Text variant='labelSmall' color='black' numberOfLines={1}>
              <Highlighter
                autoEscape
                highlightStyle={{
                  color: theme.colors.black,
                  backgroundColor: theme.colors.yellowBright,
                }}
                searchWords={xfilterVal.split(' ') || ''}
                textToHighlight={name}
              />
            </Text>
            {size > 0 && (
              <Text variant='metadataSecondary' color='textSecondary'>
                {formatSize(size)}
              </Text>
            )}
          </Box>
          {onDelete && (
            <TouchableOpacity
              onPress={() => onDelete(document as LocalFile)}
              hitSlop={{ top: 10, bottom: 10, right: 10, left: 10 }}>
              <Icon
                name='X2'
                variant='l'
                marginTop='xxxs'
                color='textPrimary'
              />
            </TouchableOpacity>
          )}
          <UploadProgressOverlay
            showProgressBar={status === 'compressing' || status === 'uploading'}
            progress={combinedProgress}
            showSuccessIcon={isLocal && status === 'success'}
            showErrorRetry={status === 'error'}
            onRetry={() => retryUpload?.(document as LocalFile)}
          />
        </Box>
        <Box marginLeft='xs' flex={1}></Box>
      </Box>
    </TouchableOpacity>
  );
};

export default DocumentCard;
